/**
 * Custom Review Tab
 * Exam-prep style: pick scope (course or CEFR/grammar), filter by familiarity,
 * choose stages, hand off to FlashcardPracticePage.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import reviewCenterService, {
  ReviewScope,
  ReviewItemSummary,
  ItemType,
  PatternCategory,
  SentenceDrillItem,
} from '../../../services/reviewCenterService';
import SearchBar from '../../../components/common/SearchBar';
import { getSmallThumbnailUrl } from '../../../utils/mediaUrl';
import './CustomTab.css';

type CourseViewMode = 'grid4' | 'grid8' | 'grid16' | 'list';
const VIEW_MODE_KEY = 'review.customTab.courseViewMode';
const VIEW_MODES: CourseViewMode[] = ['grid4', 'grid8', 'grid16', 'list'];

type ScopeMode = 'course' | 'cefrGrammar';
type Stage = 'speaking' | 'dictation' | 'meaning' | 'fillBlank';

const ALL_TYPES: ItemType[] = ['vocabulary', 'knowledgePoint', 'pattern'];
const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

const CustomTab: React.FC = () => {
  const { t } = useTranslation('review');
  const navigate = useNavigate();

  const [scope, setScope] = useState<ReviewScope | null>(null);
  const [scopeError, setScopeError] = useState<string | null>(null);
  const [scopeLoading, setScopeLoading] = useState(true);

  // Filter state
  const [scopeMode, setScopeMode] = useState<ScopeMode>('course');
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [selectedCefrs, setSelectedCefrs] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<PatternCategory[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<ItemType[]>(['vocabulary', 'knowledgePoint', 'pattern']);
  const [retrievabilityMax, setRetrievabilityMax] = useState<number>(80);
  const [includeUnpracticed, setIncludeUnpracticed] = useState<boolean>(false);
  const [count, setCount] = useState<number>(30);
  const [stages, setStages] = useState<Stage[]>(['meaning', 'fillBlank', 'dictation', 'speaking']);
  const [courseSearch, setCourseSearch] = useState<string>('');
  const [courseViewMode, setCourseViewMode] = useState<CourseViewMode>(() => {
    try {
      const saved = localStorage.getItem(VIEW_MODE_KEY);
      return VIEW_MODES.includes(saved as CourseViewMode) ? (saved as CourseViewMode) : 'list';
    } catch {
      return 'list';
    }
  });

  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, courseViewMode); } catch { /* ignore */ }
  }, [courseViewMode]);

  // Items state
  const [items, setItems] = useState<ReviewItemSummary[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  // Sentence drill items — surfaced in the matched list so learners can see
  // exactly what 聽寫整句 / 跟讀整句 will pull. Re-fetched whenever the scope
  // or stage selection changes; empty when neither speaking nor dictation
  // is part of the chosen stages.
  const [sentenceDrillItems, setSentenceDrillItems] = useState<SentenceDrillItem[]>([]);

  // Load scope once
  useEffect(() => {
    let cancelled = false;
    reviewCenterService
      .getCustomScope()
      .then(data => {
        if (cancelled) return;
        setScope(data);
        // Default: nothing selected — let the learner deliberately pick scope
        // (auto-select-all dumped 1000+ items into the list, which felt like
        // a recommendation rather than a choice).
        setSelectedCourses([]);
        setSelectedCefrs([...CEFR_LEVELS]);
      })
      .catch(err => {
        if (!cancelled) setScopeError(err?.message || t('custom.scopeLoadError'));
      })
      .finally(() => {
        if (!cancelled) setScopeLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  // Re-fetch items whenever filter changes
  useEffect(() => {
    if (!scope) return;
    let cancelled = false;
    setItemsLoading(true);
    setItemsError(null);
    reviewCenterService
      .getCustomItems({
        scopeMode,
        courseIds: scopeMode === 'course' ? selectedCourses : [],
        cefrLevels: scopeMode === 'cefrGrammar' ? selectedCefrs : [],
        patternCategories: scopeMode === 'cefrGrammar' ? selectedCategories : [],
        types: selectedTypes,
        retrievabilityMax: retrievabilityMax / 100,
        includeUnpracticed,
      })
      .then(data => {
        if (!cancelled) setItems(data.items);
      })
      .catch(err => {
        if (!cancelled) setItemsError(err?.message || t('custom.itemsLoadError'));
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    scope,
    scopeMode,
    selectedCourses,
    selectedCefrs,
    selectedCategories,
    selectedTypes,
    retrievabilityMax,
    includeUnpracticed,
    t,
  ]);

  // Re-fetch sentence drill candidates when scope or speech-stage selection
  // changes. Without this list visible in the matched items section, the
  // learner can't tell whether 聽寫整句 / 跟讀整句 will actually pull anything
  // from their selected courses (the practice queue could end up empty).
  useEffect(() => {
    if (!scope) return;
    const wantsSentenceDrill = stages.includes('speaking') || stages.includes('dictation');
    if (!wantsSentenceDrill) {
      setSentenceDrillItems([]);
      return;
    }
    let cancelled = false;
    reviewCenterService
      .getSentenceDrill(
        {
          scopeMode,
          courseIds: scopeMode === 'course' ? selectedCourses : [],
          cefrLevels: scopeMode === 'cefrGrammar' ? selectedCefrs : [],
        },
        100,
      )
      .then(data => {
        if (!cancelled) setSentenceDrillItems(data.items);
      })
      .catch(err => {
        // Non-fatal: just hide sentence preview if request fails.
        console.error('[review/custom] sentence drill preview fetch failed:', err);
        if (!cancelled) setSentenceDrillItems([]);
      });
    return () => { cancelled = true; };
  }, [scope, scopeMode, selectedCourses, selectedCefrs, stages]);

  const filteredCourses = useMemo(() => {
    if (!scope) return [];
    const byStatus = includeCompleted ? scope.courses : scope.courses.filter(c => c.status === 'in_progress');
    const q = courseSearch.trim().toLowerCase();
    if (!q) return byStatus;
    // Match across title, tags (comma-separated), CEFR level, and series title —
    // 大家說英語 / ABC Interactive English etc. are stored on `tags` or series, not title.
    return byStatus.filter(c => {
      const haystack = [
        c.title,
        ...(c.tags ?? []),
        c.cefrLevel ?? '',
        c.seriesTitle ?? '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [scope, includeCompleted, courseSearch]);

  const buckets = useMemo(() => {
    const counts = [0, 0, 0, 0, 0]; // 0-20, 20-40, 40-60, 60-80, 80-100
    for (const item of items) {
      const r = (item.retrievability ?? 0) * 100;
      const idx = Math.min(4, Math.floor(r / 20));
      counts[idx]++;
    }
    return counts;
  }, [items]);

  const stagesAvailable = useMemo<Stage[]>(() => {
    const out: Stage[] = ['meaning', 'fillBlank'];
    if (selectedTypes.includes('vocabulary')) out.push('speaking');
    if (selectedTypes.includes('vocabulary') || selectedTypes.includes('pattern')) out.push('dictation');
    return out;
  }, [selectedTypes]);

  // Drop unavailable stages whenever item types change
  useEffect(() => {
    setStages(prev => {
      const filtered = prev.filter(s => stagesAvailable.includes(s));
      if (filtered.length === 0) return ['meaning'];
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [stagesAvailable]);

  const toggleCourse = (id: string) => {
    setSelectedCourses(prev => (prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]));
  };
  const toggleCefr = (cefr: string) => {
    setSelectedCefrs(prev => (prev.includes(cefr) ? prev.filter(c => c !== cefr) : [...prev, cefr]));
  };
  const toggleCategory = (cat: PatternCategory) => {
    setSelectedCategories(prev => (prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]));
  };
  const toggleType = (type: ItemType) => {
    setSelectedTypes(prev =>
      prev.includes(type) ? (prev.length > 1 ? prev.filter(t => t !== type) : prev) : [...prev, type],
    );
  };
  const toggleStage = (stage: Stage) => {
    setStages(prev =>
      prev.includes(stage) ? (prev.length > 1 ? prev.filter(s => s !== stage) : prev) : [...prev, stage],
    );
  };

  const handleStart = () => {
    const totalAvailable = items.length + sentenceDrillItems.length;
    if (totalAvailable === 0) return;

    // Budget: split the user's count target between vocab/KP/pattern items
    // and sentence drill items proportionally. When speech stages drive
    // most of the session (e.g. only 口說 selected), let sentences claim
    // up to all the count. When quiz stages dominate, prefer items.
    const wantsSpeech = stages.includes('speaking') || stages.includes('dictation');
    const wantsQuiz = stages.includes('meaning') || stages.includes('fillBlank');

    let itemsTarget = items.length;
    let sentenceTarget = sentenceDrillItems.length;
    if (wantsSpeech && wantsQuiz) {
      // Mixed session — keep half/half balance, capped by what's actually
      // available on each side.
      itemsTarget = Math.min(items.length, Math.ceil(count / 2));
      sentenceTarget = Math.min(sentenceDrillItems.length, count - itemsTarget);
    } else if (wantsSpeech && !wantsQuiz) {
      // Speech-only — drain sentences first; vocab can fill if needed for
      // word-level shadow/dictation when sentences run out.
      sentenceTarget = Math.min(sentenceDrillItems.length, count);
      itemsTarget = Math.min(items.length, Math.max(0, count - sentenceTarget));
    } else {
      // Quiz-only — sentences don't apply.
      sentenceTarget = 0;
      itemsTarget = Math.min(items.length, count);
    }

    const top = items.slice(0, itemsTarget);
    const sentencesTop = sentenceDrillItems.slice(0, sentenceTarget);

    const params = new URLSearchParams();
    const vocabIds = top.filter(i => i.type === 'vocabulary').map(i => i.id);
    const kpIds = top.filter(i => i.type === 'knowledgePoint').map(i => i.id);
    const patternIds = top.filter(i => i.type === 'pattern').map(i => i.id);
    if (vocabIds.length) params.set('vocabIds', vocabIds.join(','));
    if (kpIds.length) params.set('kpIds', kpIds.join(','));
    if (patternIds.length) params.set('patternIds', patternIds.join(','));

    params.set('mode', 'smart');
    params.set('count', String(Math.max(itemsTarget + sentenceTarget, 5)));
    params.set('stages', stages.join(','));
    // Sentence drill items piggyback through router state instead of the
    // URL — full sentence text would blow past URL length limits.
    navigate(
      `/vocabulary-book/practice?${params.toString()}`,
      sentencesTop.length > 0 ? { state: { sentences: sentencesTop } } : undefined,
    );
  };

  if (scopeLoading) {
    return (
      <div className="rc-loading">
        <div className="rc-spinner" aria-hidden="true" />
        <p>{t('custom.loading')}</p>
      </div>
    );
  }

  if (scopeError || !scope) {
    return (
      <div className="rc-error">
        <p>{scopeError || t('custom.scopeLoadError')}</p>
      </div>
    );
  }

  return (
    <div className="rc-custom">
      {/* Step 1 — scope mode toggle */}
      <section className="rc-section">
        <h3 className="rc-section__title">{t('custom.scopeTitle')}</h3>
        <div className="rc-segmented" role="tablist">
          <button
            role="tab"
            aria-selected={scopeMode === 'course'}
            className={`rc-seg ${scopeMode === 'course' ? 'rc-seg--active' : ''}`}
            onClick={() => setScopeMode('course')}
          >
            {t('custom.scopeByCourse')}
          </button>
          <button
            role="tab"
            aria-selected={scopeMode === 'cefrGrammar'}
            className={`rc-seg ${scopeMode === 'cefrGrammar' ? 'rc-seg--active' : ''}`}
            onClick={() => setScopeMode('cefrGrammar')}
          >
            {t('custom.scopeByCefr')}
          </button>
        </div>

        {scopeMode === 'course' ? (
          <div className="rc-course-block">
            <label className="rc-checkbox">
              <input
                type="checkbox"
                checked={includeCompleted}
                onChange={e => setIncludeCompleted(e.target.checked)}
              />
              <span>{t('custom.includeCompleted')}</span>
            </label>
            <SearchBar
              value={courseSearch}
              onChange={setCourseSearch}
              onClear={() => setCourseSearch('')}
              placeholder={t('custom.searchCoursesPlaceholder')}
              className="rc-course-search"
            />
            <div className="rc-bulk-actions">
              <button
                type="button"
                className="rc-bulk-btn"
                onClick={() => setSelectedCourses(prev => Array.from(new Set([
                  ...prev,
                  ...filteredCourses.map(c => c.id),
                ])))}
                disabled={filteredCourses.every(c => selectedCourses.includes(c.id))}
              >
                {t('custom.selectAll')}
              </button>
              <button
                type="button"
                className="rc-bulk-btn"
                onClick={() => setSelectedCourses(prev => prev.filter(id => !filteredCourses.some(c => c.id === id)))}
                disabled={!filteredCourses.some(c => selectedCourses.includes(c.id))}
              >
                {t('custom.deselectAll')}
              </button>
              <span className="rc-bulk-count">
                {t('custom.selectedCount', { count: selectedCourses.length, total: scope.courses.length })}
              </span>
              <CourseViewSwitcher mode={courseViewMode} onChange={setCourseViewMode} t={t} />
            </div>
            {filteredCourses.length === 0 ? (
              <p className="rc-empty-hint">
                {courseSearch ? t('custom.noCoursesMatch') : t('custom.noCourses')}
              </p>
            ) : (
              <div className={`rc-course-scroll rc-course-scroll--${courseViewMode}`}>
                {courseViewMode === 'list' ? (
                  <div className="rc-course-list">
                    {filteredCourses.map(c => (
                      <button
                        key={c.id}
                        className={`rc-course-row ${selectedCourses.includes(c.id) ? 'rc-course-row--active' : ''}`}
                        onClick={() => toggleCourse(c.id)}
                      >
                        <span className="rc-course-row__title">{c.title}</span>
                        {c.cefrLevel && <span className="rc-course-row__cefr">{c.cefrLevel}</span>}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className={`rc-course-grid rc-course-grid--${courseViewMode}`}>
                    {filteredCourses.map(c => (
                      <button
                        key={c.id}
                        className={`rc-course-tile ${selectedCourses.includes(c.id) ? 'rc-course-tile--active' : ''}`}
                        onClick={() => toggleCourse(c.id)}
                        title={c.title}
                      >
                        <div className="rc-course-tile__thumb">
                          <img src={getSmallThumbnailUrl(c.thumbnailUrl)} alt="" loading="lazy" />
                          {c.cefrLevel && <span className="rc-course-tile__cefr">{c.cefrLevel}</span>}
                          {selectedCourses.includes(c.id) && (
                            <span className="rc-course-tile__check" aria-hidden="true">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                              </svg>
                            </span>
                          )}
                        </div>
                        {courseViewMode !== 'grid16' && (
                          <span className="rc-course-tile__title">{c.title}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="rc-cefr-block">
            <div className="rc-sublabel">{t('custom.cefrLabel')}</div>
            <div className="rc-chip-grid">
              {CEFR_LEVELS.map(cefr => (
                <button
                  key={cefr}
                  className={`rc-chip rc-chip--cefr ${selectedCefrs.includes(cefr) ? 'rc-chip--active' : ''}`}
                  onClick={() => toggleCefr(cefr)}
                >
                  {cefr}
                </button>
              ))}
            </div>
            <div className="rc-sublabel">{t('custom.categoryLabel')}</div>
            <div className="rc-chip-grid">
              {scope.patternCategories.map(cat => (
                <button
                  key={cat}
                  className={`rc-chip ${selectedCategories.includes(cat) ? 'rc-chip--active' : ''}`}
                  onClick={() => toggleCategory(cat)}
                >
                  {t(`custom.patternCategory.${cat}`, { defaultValue: cat })}
                </button>
              ))}
            </div>
            {selectedCategories.length === 0 && (
              <p className="rc-empty-hint">{t('custom.allCategoriesHint')}</p>
            )}
          </div>
        )}
      </section>

      {/* Step 2 — type filter */}
      <section className="rc-section">
        <h3 className="rc-section__title">{t('custom.typeTitle')}</h3>
        <div className="rc-chip-grid">
          {ALL_TYPES.map(type => (
            <button
              key={type}
              className={`rc-chip ${selectedTypes.includes(type) ? 'rc-chip--active' : ''}`}
              onClick={() => toggleType(type)}
            >
              {t(`itemType.${type}`)}
            </button>
          ))}
        </div>
      </section>

      {/* Step 3 — familiarity filter + buckets */}
      <section className="rc-section">
        <h3 className="rc-section__title">{t('custom.familiarityTitle')}</h3>
        <label className="rc-checkbox rc-checkbox--prominent">
          <input
            type="checkbox"
            checked={includeUnpracticed}
            onChange={e => setIncludeUnpracticed(e.target.checked)}
          />
          <span>{t('custom.includeUnpracticed')}</span>
          <span className="rc-checkbox__hint">{t('custom.includeUnpracticedHint')}</span>
        </label>
        <div className="rc-slider-row">
          <input
            type="range"
            min={20}
            max={100}
            step={5}
            value={retrievabilityMax}
            onChange={e => setRetrievabilityMax(Number(e.target.value))}
            className="rc-slider"
            aria-label={t('custom.familiaritySliderAria')}
          />
          <span className="rc-slider__value">≤ {retrievabilityMax}%</span>
        </div>
        <div className="rc-bucket-bar" aria-hidden="true">
          {buckets.map((c, i) => {
            const max = Math.max(1, ...buckets);
            const h = (c / max) * 100;
            const colors = ['#dc2626', '#f97316', '#f59e0b', '#84cc16', '#16a34a'];
            return (
              <div key={i} className="rc-bucket-col" title={`${i * 20}-${(i + 1) * 20}%: ${c}`}>
                <div className="rc-bucket-bar__fill" style={{ height: `${h}%`, background: colors[i] }} />
                <div className="rc-bucket-label">{i * 20}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Step 4 — items list summary (vocab/KP/pattern + sentence drill) */}
      <section className="rc-section">
        <h3 className="rc-section__title">
          {t('custom.matchedTitle', { count: items.length + sentenceDrillItems.length })}
        </h3>
        {itemsLoading ? (
          <div className="rc-loading">
            <div className="rc-spinner" aria-hidden="true" />
            <p>{t('custom.loadingItems')}</p>
          </div>
        ) : itemsError ? (
          <p className="rc-empty-hint">{itemsError}</p>
        ) : (items.length + sentenceDrillItems.length) === 0 ? (
          <p className="rc-empty-hint">{t('custom.noMatches')}</p>
        ) : (
          <div className="rc-items">
            {items.slice(0, 50).map(item => (
              <div key={`${item.type}-${item.id}`} className="rc-item-row">
                <span className={`rc-type-tag rc-type-${item.type === 'vocabulary' ? 'vocab' : item.type === 'knowledgePoint' ? 'kp' : 'pattern'}`}>
                  {t(`itemType.${item.type}`)}
                </span>
                <span className="rc-item-term">{item.term}</span>
                {item.isUnpracticed ? (
                  <span className="rc-item-new">{t('custom.unpracticedTag')}</span>
                ) : (
                  <span className="rc-item-r" style={{ color: rColor(item.retrievability ?? 0) }}>
                    {((item.retrievability ?? 0) * 100).toFixed(0)}%
                  </span>
                )}
              </div>
            ))}
            {items.length > 50 && (
              <p className="rc-empty-hint">{t('custom.moreItems', { count: items.length - 50 })}</p>
            )}
            {/* Sentence drill items appear with their own type tag so the
                learner can see the sentences pulled by 聽寫整句 / 跟讀整句
                before clicking start. Capped at 50 in the same row budget
                as vocab/KP/pattern. */}
            {sentenceDrillItems.slice(0, Math.max(0, 50 - items.length)).map(s => (
              <div key={`sentence-${s.id}`} className="rc-item-row">
                <span className="rc-type-tag rc-type-sentence" style={{ background: '#fef3c7', color: '#92400e' }}>
                  {t('custom.sentenceTag', { defaultValue: '句子' })}
                </span>
                <span className="rc-item-term" title={s.courseTitle}>{s.sentenceText}</span>
                {s.isErrorSentence ? (
                  <span className="rc-item-r" style={{ color: '#dc2626' }} title={t('custom.sentenceErrorTooltip', { defaultValue: '之前答錯' })}>
                    ⟳
                  </span>
                ) : (
                  <span className="rc-item-new">{t('custom.unpracticedTag')}</span>
                )}
              </div>
            ))}
            {sentenceDrillItems.length > Math.max(0, 50 - items.length) && (
              <p className="rc-empty-hint">
                {t('custom.moreSentenceItems', {
                  defaultValue: '... 還有 {{count}} 句',
                  count: sentenceDrillItems.length - Math.max(0, 50 - items.length),
                })}
              </p>
            )}
          </div>
        )}
      </section>

      {/* Step 5 — stages */}
      <section className="rc-section">
        <h3 className="rc-section__title">{t('custom.stageTitle')}</h3>
        <div className="rc-chip-grid">
          {(['meaning', 'fillBlank', 'dictation', 'speaking'] as Stage[]).map(stage => {
            const enabled = stagesAvailable.includes(stage);
            return (
              <button
                key={stage}
                disabled={!enabled}
                className={`rc-chip ${stages.includes(stage) ? 'rc-chip--active' : ''} ${!enabled ? 'rc-chip--disabled' : ''}`}
                onClick={() => enabled && toggleStage(stage)}
                title={!enabled ? t('custom.stageNotApplicable') : undefined}
              >
                {t(`stage.${stage}`)}
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 6 — count + start */}
      <section className="rc-section rc-section--actions">
        <label className="rc-count">
          <span className="rc-count__label">{t('custom.countLabel')}</span>
          <input
            type="number"
            min={5}
            max={Math.max(items.length + sentenceDrillItems.length, 5)}
            step={5}
            value={count}
            onChange={e => setCount(Math.max(5, Number(e.target.value) || 30))}
            className="rc-count__input"
          />
        </label>
        <button
          className="rc-start-btn"
          onClick={handleStart}
          disabled={(items.length + sentenceDrillItems.length) === 0}
        >
          {t('custom.startBtn', { count: Math.min(items.length + sentenceDrillItems.length, count) })}
        </button>
      </section>
    </div>
  );
};

const rColor = (r: number) => (r >= 0.9 ? '#16a34a' : r >= 0.7 ? '#f59e0b' : '#dc2626');

/* ── View mode switcher ─────────────────────────── */
const CourseViewSwitcher: React.FC<{
  mode: CourseViewMode;
  onChange: (mode: CourseViewMode) => void;
  t: (key: string, opts?: any) => string;
}> = ({ mode, onChange, t }) => {
  const renderBtn = (m: CourseViewMode, icon: React.ReactNode, labelKey: string) => (
    <button
      type="button"
      className={`rc-view-btn ${mode === m ? 'rc-view-btn--active' : ''}`}
      onClick={() => onChange(m)}
      aria-label={t(labelKey)}
      title={t(labelKey)}
    >
      {icon}
    </button>
  );

  return (
    <div className="rc-view-switcher" role="group">
      {renderBtn(
        'grid4',
        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="1" y="1" width="5.5" height="5.5" rx="1" /><rect x="7.5" y="1" width="5.5" height="5.5" rx="1" /><rect x="1" y="7.5" width="5.5" height="5.5" rx="1" /><rect x="7.5" y="7.5" width="5.5" height="5.5" rx="1" /></svg>,
        'custom.viewGrid4',
      )}
      {renderBtn(
        'grid8',
        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">{[0,1,2,3].map(r => [0,1].map(c => <rect key={`${r}-${c}`} x={1 + c * 6.5} y={1 + r * 3} width="5" height="2.5" rx="0.5" />))}</svg>,
        'custom.viewGrid8',
      )}
      {renderBtn(
        'grid16',
        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">{[0,1,2,3].map(r => [0,1,2,3].map(c => <rect key={`${r}-${c}`} x={1 + c * 3.25} y={1 + r * 3.25} width="2.5" height="2.5" rx="0.4" />))}</svg>,
        'custom.viewGrid16',
      )}
      {renderBtn(
        'list',
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M2 3.5h10M2 7h10M2 10.5h10" /></svg>,
        'custom.viewList',
      )}
    </div>
  );
};

export default CustomTab;
